/**
 * A labelled donut: Vega-Lite arc plus an HTML key.
 *
 * The key is HTML rather than a Vega legend so a later slice that needs a
 * non-colour cue can carry one — same reason {@link VegaChart}'s shared config
 * draws no legends. Specs are built here and handed to {@link VegaChart};
 * callers never author Vega-Lite for a donut.
 *
 * Slice values tween when they change, and the live Vega view is patched rather
 * than re-embedded, so filters animate instead of jumping.
 */

import { DotIcon } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import VegaChart, { type ChartSpec } from './VegaChart';

export type DonutSegment = {
  label: string;
  value: number;
  color: string;
};

type DonutChartProps = {
  segments: readonly DonutSegment[];
  /** Names the figure for assistive technology, which cannot read the marks. */
  ariaLabel: string;
  title?: string;
  subtitle?: string;
  centerValue?: string | number;
  centerLabel?: string;
  emptyMessage?: string;
  size?: number;
  tooltipCategoryTitle?: string;
  tooltipValueTitle?: string;
  className?: string;
  /** Tween slice values when they change. Defaults to true. */
  animating?: boolean;
};

const DONUT_DATA = 'donut';
const ANIMATION_MS = 420;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function segmentsKey(segments: readonly DonutSegment[]): string {
  return segments.map((segment) => `${segment.label}:${segment.value}:${segment.color}`).join('|');
}

function useAnimatedSegments(segments: readonly DonutSegment[], animating: boolean): DonutSegment[] {
  const [displayed, setDisplayed] = useState<DonutSegment[]>(() => [...segments]);
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;
  const targetKey = segmentsKey(segments);

  useEffect(() => {
    if (!animating) {
      setDisplayed([...segments]);
      return;
    }

    const from = new Map(displayedRef.current.map((segment) => [segment.label, segment.value]));
    const started = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / ANIMATION_MS);
      const eased = easeOutCubic(t);
      setDisplayed(
        segments.map((segment) => ({
          ...segment,
          value: (from.get(segment.label) ?? 0) + (segment.value - (from.get(segment.label) ?? 0)) * eased
        }))
      );
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animating, segments, targetKey]);

  return animating ? displayed : [...segments];
}

function donutSpec(
  segments: readonly DonutSegment[],
  size: number,
  tooltipCategoryTitle: string,
  tooltipValueTitle: string
): ChartSpec {
  const outerRadius = Math.round(size * 0.48);
  const innerRadius = Math.round(size * 0.32);
  return {
    width: size,
    height: size,
    padding: 0,
    data: { name: DONUT_DATA },
    mark: {
      type: 'arc',
      innerRadius,
      outerRadius,
      // Same-colour stroke hides the stair-step on neighbouring slice edges.
      strokeWidth: 1,
      strokeJoin: 'round',
      tooltip: true
    },
    encoding: {
      theta: { field: 'count', type: 'quantitative', stack: true },
      // Segment array order, clockwise from 12 o'clock — not by slice size.
      order: { field: 'rank', type: 'quantitative', sort: 'ascending' },
      color: {
        field: 'category',
        type: 'nominal',
        scale: {
          domain: segments.map((segment) => segment.label),
          range: segments.map((segment) => segment.color)
        },
        legend: null
      },
      stroke: {
        field: 'category',
        type: 'nominal',
        scale: {
          domain: segments.map((segment) => segment.label),
          range: segments.map((segment) => segment.color)
        },
        legend: null
      },
      tooltip: [
        { field: 'category', title: tooltipCategoryTitle },
        { field: 'count', title: tooltipValueTitle, format: 'd' }
      ]
    }
  };
}

export default function DonutChart({
  segments,
  ariaLabel,
  title,
  subtitle,
  centerValue,
  centerLabel,
  emptyMessage = 'Nothing to show',
  size = 120,
  tooltipCategoryTitle = 'Category',
  tooltipValueTitle = 'Count',
  className,
  animating = true
}: DonutChartProps) {
  const displayed = useAnimatedSegments(segments, animating);
  const targetTotal = segments.reduce((sum, segment) => sum + segment.value, 0);
  const displayedTotal = displayed.reduce((sum, segment) => sum + segment.value, 0);
  const scaleKey = segments.map((segment) => `${segment.label}:${segment.color}`).join('|');
  const spec = useMemo(
    () => donutSpec(segments, size, tooltipCategoryTitle, tooltipValueTitle),
    // Scale (labels/colours) is what the spec encodes; values are patched onto the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scaleKey stands in for segments' identity
    [scaleKey, size, tooltipCategoryTitle, tooltipValueTitle]
  );
  const values = useMemo(
    () =>
      displayed
        .filter((segment) => segment.value > 0.01)
        .map((segment) => ({
          category: segment.label,
          count: segment.value,
          rank: segments.findIndex((item) => item.label === segment.label)
        })),
    [displayed, segments]
  );

  const shownCenter =
    centerValue === undefined ? undefined : typeof centerValue === 'number' ? Math.round(displayedTotal) : centerValue;

  return (
    <div
      className={twMerge(
        'flex flex-col justify-center gap-2 rounded-xl border-2 border-border-subtle px-3 py-2',
        className
      )}
    >
      {(title || subtitle) && (
        <div>
          {title && <p className="text-sm font-semibold text-content-default">{title}</p>}
          {subtitle && <p className="mt-0.5 text-xs text-content-subtle">{subtitle}</p>}
        </div>
      )}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          {targetTotal > 0 || displayedTotal > 0.5 ? (
            <>
              <VegaChart
                spec={spec}
                ariaLabel={ariaLabel}
                dataName={DONUT_DATA}
                values={values}
                className="[&_svg]:[shape-rendering:geometricPrecision]"
              />
              {(shownCenter !== undefined || centerLabel) && (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  {shownCenter !== undefined && (
                    <span className="text-lg font-semibold leading-none text-content-default">{shownCenter}</span>
                  )}
                  {centerLabel && (
                    <span className="mt-0.5 text-[10px] uppercase tracking-wide text-content-subtle">
                      {centerLabel}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-center text-xs text-content-subtle">
              {emptyMessage}
            </div>
          )}
        </div>
        <div className="min-w-0 pr-2">
          <ul className="space-y-1">
            {displayed.map((segment) => (
              <li key={segment.label} className="flex items-center gap-2 text-xs text-content-default">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: segment.color }}
                  aria-hidden
                />
                <span className="inline-flex items-center">
                  {segment.label}
                  <DotIcon size={14} weight="light" className="text-content-subtle" aria-hidden />
                  <span className="text-content-subtle">{Math.round(segment.value)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
